import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { Empty } from '@creditornot/cb-placeholders';
import { Spinner } from '@creditornot/cb-progress';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import styled from 'styled-components';

import { getProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import { usePluralization } from 'common/hooks';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { useMenuModuleContext } from 'modules/menu/context';

import ItemRowRenderer from './ItemRowRenderer';
import ProgressTracking from './ProgressTracking';
import SelectAndSearch from './SelectAndSearch';
import useAddItemsToCatalog from '../useAddItemsToCatalog';

type Props = {
  items: Item[];
  onClose: () => void;
};

const StyledWoltModal = styled(WoltModal)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  width: 100%;
  max-width: 640px;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const AlertBar = styled.div`
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 456px;
  overflow: auto;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const StyledErrorNotification = styled(APIErrorNotification)`
  margin-block-end: 16px;
`;

const SpinnerContainer = styled.div`
  height: 576px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledEmpty = styled(Empty)`
  flex: 1;
`;

const ErrorWrapper = styled.div`
  margin-block-start: 8px;
`;

const Error = styled.div`
  margin-block-start: 8px;
`;

const ErrorRoot = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const AddToCatalogModal = ({ items, onClose }: Props) => {
  const { venue } = useMenuModuleContext();
  const { FormattedPluralMessage } = usePluralization();
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: productInfoSettings,
    isLoading: loadingInfoSettings,
    error: errorInfoSettings,
  } = useQuery(
    [`product-information-settings/${venue.menu}`],
    () => (venue.menu ? getProductInformationSettings(venue.menu) : undefined),
    {
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const { itemsNotInCatalog, initialLoading, addingItems, error, progressPercentage, addItemsToCatalog } =
    useAddItemsToCatalog({
      items,
      selectedItemIds,
      productHierarchyLevels: productInfoSettings?.product_hierarchy_levels ?? [],
      country: venue.country,
      onDone({ createdProducts, errorMessages, productsByError, failToUploadImagesItem }) {
        setSelectedItemIds([]);
        onClose();

        const hasCreatedProducts = createdProducts.length > 0;
        const hasProductErrorResponses = Object.keys(productsByError).length > 0;
        const hasErrorResponses = errorMessages.length > 0;
        const hasItemImagesUploadError = failToUploadImagesItem.length > 0;

        const success =
          hasCreatedProducts && !hasProductErrorResponses && !hasItemImagesUploadError && !hasErrorResponses;

        if (hasProductErrorResponses || hasErrorResponses) {
          toast.error(
            <ErrorRoot>
              <FormattedMessage id="menu.catalog.add-items-failure" />
              <ErrorWrapper>
                {hasProductErrorResponses &&
                  Object.entries(productsByError).map(([error, product]) => (
                    <Error key={error}>
                      <div>{error}</div>
                      {product.map(product => (
                        <li key={product.id}>{localize(product.attributes.name, primaryLanguage)}</li>
                      ))}
                    </Error>
                  ))}
                {hasErrorResponses && errorMessages.join('\n')}
              </ErrorWrapper>
            </ErrorRoot>,
            {
              style: { width: 400 },
              duration: Infinity,
            },
          );
        }

        if (hasItemImagesUploadError) {
          toast.error(
            <div>
              <FormattedMessage id="menu.catalog.add-item-images-failure" />
              {failToUploadImagesItem.map(item => localize(item.name, primaryLanguage)).join('\n')}
            </div>,
            {
              duration: Infinity,
            },
          );
        }

        if (success) {
          toast.success(<FormattedMessage id="menu.catalog.add-items-success" />);
        }
      },
    });

  const visibleData = useMemo(
    () =>
      itemsNotInCatalog
        ?.map(item => ({
          itemId: item.id,
          image: item.image,
          hasProductFamilyId: !!item.product_hierarchy_level_id,
          name: localize(item.name, primaryLanguage),
          gtin: item.barcode_gtin,
        }))
        .filter(
          row =>
            row.name?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase()) || row.gtin?.includes(searchQuery),
        ),
    [itemsNotInCatalog, primaryLanguage, searchQuery],
  );

  const handleSelectItemId = (itemId: string) => {
    setSelectedItemIds(prev => (prev.includes(itemId) ? prev.filter(n => n !== itemId) : [...prev, itemId]));
  };

  return (
    <StyledWoltModal isOpen onRequestClose={() => (addingItems ? undefined : onClose())}>
      <ModalHeader onCloseClick={() => (addingItems ? undefined : onClose())}>
        <FormattedMessage id="menu.catalog.add-items-title" />
      </ModalHeader>
      <StyledModalContent>
        {(initialLoading || loadingInfoSettings) && (
          <SpinnerContainer>
            <Spinner size="large" />
          </SpinnerContainer>
        )}
        {addingItems && (
          <ProgressTracking percentage={progressPercentage}>
            <FormattedPluralMessage id="menu.catalog.add-items-progress" count={selectedItemIds.length} />
          </ProgressTracking>
        )}
        {visibleData && !addingItems && (
          <>
            <AlertBar>
              <InputAlert variant="info">
                <FormattedPluralMessage id="menu.catalog.add-items-info" count={Number(itemsNotInCatalog?.length)} />
              </InputAlert>
            </AlertBar>
            <SelectAndSearch
              listData={visibleData}
              searchQuery={searchQuery}
              selectedItemIds={selectedItemIds}
              onChangeSearchQuery={setSearchQuery}
              onSelectItemIds={setSelectedItemIds}
            />
            <ListContainer>
              <AutoSizer disableWidth>
                {({ height }) => (
                  <FixedSizeList
                    height={height}
                    width="100%"
                    itemData={{
                      list: visibleData,
                      selectedItemIds,
                      onSelectItemId: handleSelectItemId,
                    }}
                    itemCount={visibleData.length}
                    itemSize={76}
                    itemKey={(index, data) => `${data.list[index].gtin}/${index}`}
                    overscanCount={6}
                  >
                    {ItemRowRenderer}
                  </FixedSizeList>
                )}
              </AutoSizer>
              {visibleData.length === 0 && <StyledEmpty />}
            </ListContainer>
          </>
        )}
      </StyledModalContent>
      <StyledModalFooter>
        {!!(errorInfoSettings || error) && <StyledErrorNotification error={errorInfoSettings || error} />}
        <Actions>
          <Button variant="secondary" size="small" onClick={onClose} disabled={addingItems}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            style={{ marginInlineStart: 16 }}
            variant="primary"
            size="small"
            disabled={addingItems || !selectedItemIds.length}
            loading={addingItems}
            onClick={() => addItemsToCatalog()}
          >
            <FormattedMessage id="state.add" />
          </Button>
        </Actions>
      </StyledModalFooter>
    </StyledWoltModal>
  );
};

export default AddToCatalogModal;
